import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Modal,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  IconButton,
  CircularProgress,
} from '@mui/material';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import { useNavigate, Navigate } from 'react-router';
import TerminalLayout from '../layout/TerminalLayout';
import TerminalOrangeButton from '../ui/TerminalOrangeButton';
import {
  useListDocsQuery,
  useFindByNumberMutation,
  useConfirmDocMutation,
} from '../../store/api/docConfirmationApi';
import { useAuth } from '../../hooks/useAuth';
import { useBarcodeScannerWaiting } from '../../hooks/useBarcodeScannerWaiting';
import { config } from '../../config/config';
import routes from '../../routes/routes';
import { dateInputToday, formatDateDisplayDots } from '../../helpers/dateHelpers';
import { formatMoney } from '../../helpers/dataFormatter';
import { getGeoCoords } from '../../helpers/geoCoords';
import {
  DOC_CONFIRMATION_STATUS_FILTER,
  loadDocConfirmationListFilters,
  saveDocConfirmationListFilters,
} from '../../helpers/docConfirmationListFiltersStorage';
import BarcodeCameraDialog from '../picking/BarcodeCameraDialog';
import DocConfirmationDocumentCard from './DocConfirmationDocumentCard';
import {
  documentCardSx,
  terminalBarIconButtonSx,
  terminalSelectSx,
  terminalSelectMenuProps,
} from '../../theme/terminalStyles';

const filterFormControlSx = {
  ...terminalSelectSx,
  flexShrink: 0,
  minWidth: { xs: 118, sm: 132 },
  '& .MuiInputLabel-root': { fontSize: { xs: '0.75rem', sm: '0.875rem' } },
  '& .MuiSelect-select, & .MuiOutlinedInput-input': {
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
    py: 0.75,
    fontWeight: 500,
  },
};

const statusFilterControlSx = {
  ...filterFormControlSx,
  ml: 'auto',
  flex: '1 1 0%',
  flexShrink: 1,
  minWidth: 0,
  maxWidth: '100%',
  '& .MuiOutlinedInput-root': {
    minWidth: 0,
    width: '100%',
  },
  '& .MuiSelect-select': {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'clip',
    display: 'block',
    maskImage: 'linear-gradient(to right, #000 calc(100% - 12px), transparent 100%)',
    WebkitMaskImage: 'linear-gradient(to right, #000 calc(100% - 12px), transparent 100%)',
  },
};

const dateFilterInputSx = {
  width: '100%',
  position: 'relative',
  fontWeight: 500,
  '& .MuiOutlinedInput-input': {
    width: '100%',
    minWidth: '10ch',
    px: 1.75,
    py: 0.75,
    fontVariantNumeric: 'tabular-nums',
    color: 'transparent',
    caretColor: 'transparent',
  },
  '& input[type="date"]::-webkit-datetime-edit': {
    padding: 0,
    margin: 0,
    visibility: 'hidden',
  },
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    opacity: 0,
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    margin: 0,
    padding: 0,
    cursor: 'pointer',
    background: 'transparent',
  },
};

const dateFilterDisplaySx = {
  position: 'absolute',
  left: 14,
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  fontSize: { xs: '0.75rem', sm: '0.875rem' },
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1,
  whiteSpace: 'nowrap',
  color: 'text.primary',
  fontWeight: 500,
};

const triggerDatePicker = (input) => {
  if (!input?.showPicker) return;
  try {
    input.showPicker();
  } catch (e) {
    /* ignore */
  }
};

const openDatePicker = (event) => {
  triggerDatePicker(event.currentTarget.querySelector('input[type="date"]'));
};

const statusColor = (doc) => {
  if (doc.isConfirmed) return '#2e7d32';
  if (doc.isUpdatedInERP) return '#9c27b0';
  return '#9e9e9e';
};

const routeLabel = (doc) => doc?.routeList?.name || doc?.routeList?.number || '—';

const DocInfoBlock = ({ doc }) => {
  if (!doc) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography>
        <strong>Номер:</strong> {doc.number || '—'}
      </Typography>
      <Typography>
        <strong>Клієнт:</strong> {doc.clientName || '—'}
      </Typography>
      <Typography>
        <strong>Сума:</strong> {formatMoney(doc.sum ?? 0)} ₴
      </Typography>
      <Typography>
        <strong>Маршрут:</strong> {routeLabel(doc)}
      </Typography>
    </Box>
  );
};

const readInitialListFilters = () => {
  const saved = loadDocConfirmationListFilters();
  const today = dateInputToday();
  return {
    dateFrom: saved?.dateFrom ?? today,
    dateTo: saved?.dateTo ?? today,
    statusFilter: saved?.statusFilter ?? DOC_CONFIRMATION_STATUS_FILTER.PENDING,
  };
};

const DocConfirmationListPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const user = authState.user;

  const [dateFrom, setDateFrom] = useState(() => readInitialListFilters().dateFrom);
  const [dateTo, setDateTo] = useState(() => readInitialListFilters().dateTo);
  const [statusFilter, setStatusFilter] = useState(() => readInitialListFilters().statusFilter);
  const [appliedFilter, setAppliedFilter] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [cameraSearchOpen, setCameraSearchOpen] = useState(false);
  const [confirmDoc, setConfirmDoc] = useState(null);
  const [alreadyDoc, setAlreadyDoc] = useState(null);
  const [scanMessage, setScanMessage] = useState('');

  useEffect(() => {
    saveDocConfirmationListFilters({ dateFrom, dateTo, statusFilter });
  }, [dateFrom, dateTo, statusFilter]);

  const queryParams = useMemo(
    () => ({
      dateFrom,
      dateTo,
      ...(appliedFilter ? { numberContains: appliedFilter } : {}),
    }),
    [dateFrom, dateTo, appliedFilter]
  );

  const { data: docs = [], isLoading, isFetching, error } = useListDocsQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });
  const [findByNumber, { isLoading: isFinding }] = useFindByNumberMutation();
  const [confirmMutation, { isLoading: isConfirming }] = useConfirmDocMutation();

  const dialogOpen = Boolean(confirmDoc || alreadyDoc || scanMessage || searchOpen || cameraSearchOpen);

  const handleScannedCode = async (raw) => {
    const code = String(raw || '').trim();
    if (!code) return;
    try {
      const found = await findByNumber(code).unwrap();
      if (found.isConfirmed || found.alreadyConfirmed) {
        setAlreadyDoc(found);
        return;
      }
      setConfirmDoc(found);
    } catch (e) {
      setScanMessage(e?.data?.message || e?.message || 'Накладну не знайдено');
    }
  };

  useBarcodeScannerWaiting(config.scanCodeLength, !dialogOpen, [dialogOpen], {
    mthd: handleScannedCode,
  });

  const filteredDocs = useMemo(() => {
    if (statusFilter === DOC_CONFIRMATION_STATUS_FILTER.ALL) return docs;
    if (statusFilter === DOC_CONFIRMATION_STATUS_FILTER.CONFIRMED) {
      return docs.filter((d) => d.isConfirmed);
    }
    return docs.filter((d) => !d.isConfirmed);
  }, [docs, statusFilter]);

  if (!user?.terminalWorkplace?.isDocConfirmation) {
    return <Navigate to={routes.root} replace />;
  }

  const handleConfirm = async () => {
    if (!confirmDoc) return;
    try {
      const coords = await getGeoCoords();
      await confirmMutation({
        docId: confirmDoc.id,
        latitude: coords.latitude,
        longitude: coords.longitude,
      }).unwrap();
      setConfirmDoc(null);
    } catch (e) {
      const msg = e?.data?.message || e?.message || 'Не вдалося підтвердити';
      if (e?.status === 409 || /вже підтверджено/i.test(msg)) {
        setConfirmDoc(null);
        setAlreadyDoc(confirmDoc);
        return;
      }
      setScanMessage(msg);
    }
  };

  const NumericSearchModal = () => {
    const [localVal, setLocalVal] = useState('');
    const keyboardRef = React.useRef(null);

    useEffect(() => {
      if (searchOpen) setLocalVal(appliedFilter);
    }, [searchOpen, appliedFilter]);

    const apply = () => {
      setAppliedFilter(localVal.trim());
      setSearchOpen(false);
    };

    return (
      <Modal open={searchOpen} onClose={() => setSearchOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '92%',
            maxWidth: 420,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Пошук за номером
          </Typography>
          <TextField
            fullWidth
            value={localVal}
            onChange={(e) => setLocalVal(e.target.value)}
            margin="normal"
            inputProps={{ inputMode: 'numeric' }}
          />
          <Keyboard
            keyboardRef={(r) => {
              keyboardRef.current = r;
            }}
            onChange={setLocalVal}
            layout={{ default: ['1 2 3', '4 5 6', '7 8 9', '{clearAll} 0 {bksp}'] }}
            display={{ '{bksp}': '⌫', '{clearAll}': 'C' }}
            theme="hg-theme-default hg-layout-numeric"
            onKeyPress={(button) => {
              if (button === '{clearAll}') {
                setLocalVal('');
                keyboardRef.current?.clearInput();
              }
            }}
          />
          <TerminalOrangeButton
            fullWidth
            sx={{ mt: 1 }}
            startIcon={<PhotoCameraIcon />}
            onClick={() => {
              setSearchOpen(false);
              setCameraSearchOpen(true);
            }}
          >
            Сканувати з камери
          </TerminalOrangeButton>
          <TerminalOrangeButton fullWidth sx={{ mt: 2 }} onClick={apply}>
            OK
          </TerminalOrangeButton>
        </Box>
      </Modal>
    );
  };

  const bottomBar = (
    <>
      <IconButton aria-label="пошук" onClick={() => setSearchOpen(true)} sx={terminalBarIconButtonSx}>
        <SearchIcon />
      </IconButton>
      <IconButton
        aria-label="камера"
        onClick={() => setCameraSearchOpen(true)}
        sx={terminalBarIconButtonSx}
      >
        <PhotoCameraIcon />
      </IconButton>
      {appliedFilter ? (
        <IconButton
          aria-label="очистити фільтр"
          onClick={() => setAppliedFilter('')}
          sx={terminalBarIconButtonSx}
        >
          <FilterAltOffIcon />
        </IconButton>
      ) : null}
    </>
  );

  return (
    <TerminalLayout
      title="Підтвердження накладних"
      titleIcon={<AssignmentTurnedInOutlinedIcon />}
      backTo={routes.root}
      bottomBar={bottomBar}
    >
      <BarcodeCameraDialog
        open={cameraSearchOpen}
        onClose={() => setCameraSearchOpen(false)}
        onCode={(text) => {
          setCameraSearchOpen(false);
          handleScannedCode(text);
        }}
        title="Сканувати номер / штрихкод"
      />
      <NumericSearchModal />

      <Paper sx={{ ...documentCardSx, mb: 1.5, pt: 1.5, pb: 1, px: 1.5, overflow: 'visible' }} elevation={0}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 },
            width: '100%',
            minWidth: 0,
          }}
        >
          <FormControl
            size="small"
            sx={{ ...filterFormControlSx, minWidth: { xs: 132, sm: 148 } }}
            onClick={openDatePicker}
          >
            <InputLabel htmlFor="doc-conf-date-from" shrink>
              З дати
            </InputLabel>
            <Box sx={{ position: 'relative', width: '100%' }}>
              <OutlinedInput
                id="doc-conf-date-from"
                type="date"
                label="З дати"
                notched
                value={dateFrom}
                onChange={(e) => {
                  const next = e.target.value;
                  if (!next) return;
                  setDateFrom(next);
                  if (dateTo && next > dateTo) setDateTo(next);
                }}
                onKeyDown={(e) => e.preventDefault()}
                sx={dateFilterInputSx}
                inputProps={{
                  onClick: (e) => {
                    e.stopPropagation();
                    triggerDatePicker(e.currentTarget);
                  },
                }}
              />
              <Typography component="span" aria-hidden sx={dateFilterDisplaySx}>
                {formatDateDisplayDots(dateFrom)}
              </Typography>
            </Box>
          </FormControl>
          <FormControl
            size="small"
            sx={{ ...filterFormControlSx, minWidth: { xs: 132, sm: 148 } }}
            onClick={openDatePicker}
          >
            <InputLabel htmlFor="doc-conf-date-to" shrink>
              По дату
            </InputLabel>
            <Box sx={{ position: 'relative', width: '100%' }}>
              <OutlinedInput
                id="doc-conf-date-to"
                type="date"
                label="По дату"
                notched
                value={dateTo}
                onChange={(e) => {
                  const next = e.target.value;
                  if (!next) return;
                  setDateTo(next);
                  if (dateFrom && next < dateFrom) setDateFrom(next);
                }}
                onKeyDown={(e) => e.preventDefault()}
                sx={dateFilterInputSx}
                inputProps={{
                  onClick: (e) => {
                    e.stopPropagation();
                    triggerDatePicker(e.currentTarget);
                  },
                }}
              />
              <Typography component="span" aria-hidden sx={dateFilterDisplaySx}>
                {formatDateDisplayDots(dateTo)}
              </Typography>
            </Box>
          </FormControl>
          <FormControl size="small" sx={statusFilterControlSx}>
            <InputLabel id="doc-conf-status-label">Статус</InputLabel>
            <Select
              labelId="doc-conf-status-label"
              label="Статус"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              MenuProps={terminalSelectMenuProps}
              fullWidth
            >
              <MenuItem value={DOC_CONFIRMATION_STATUS_FILTER.PENDING}>Непідтверджені</MenuItem>
              <MenuItem value={DOC_CONFIRMATION_STATUS_FILTER.ALL}>Всі</MenuItem>
              <MenuItem value={DOC_CONFIRMATION_STATUS_FILTER.CONFIRMED}>Підтверджені</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">Не вдалося завантажити список</Typography>
      ) : (
        <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
          {filteredDocs.map((doc) => (
            <DocConfirmationDocumentCard
              key={doc.id}
              doc={doc}
              statusColor={statusColor(doc)}
              onView={() => navigate(`${routes.docConfirmation}/${doc.id}`)}
              onConfirm={() => setConfirmDoc(doc)}
            />
          ))}
          {!filteredDocs.length ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              Документів не знайдено
            </Typography>
          ) : null}
        </Box>
      )}

      {isFetching && !isLoading ? (
        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
          Оновлення…
        </Typography>
      ) : null}
      {isFinding ? (
        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
          Пошук за штрихкодом…
        </Typography>
      ) : null}

      <Dialog open={Boolean(confirmDoc)} onClose={() => setConfirmDoc(null)}>
        <DialogTitle>Підтвердити документ?</DialogTitle>
        <DialogContent>
          <DocInfoBlock doc={confirmDoc} />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <TerminalOrangeButton
            onClick={() => setConfirmDoc(null)}
            sx={{ bgcolor: 'transparent', color: 'text.primary', boxShadow: 'none' }}
          >
            Ні
          </TerminalOrangeButton>
          <TerminalOrangeButton
            onClick={() => {
              const id = confirmDoc?.id;
              setConfirmDoc(null);
              if (id) navigate(`${routes.docConfirmation}/${id}`);
            }}
            sx={{ bgcolor: 'transparent', color: 'text.primary', boxShadow: 'none' }}
          >
            Переглянути
          </TerminalOrangeButton>
          <TerminalOrangeButton onClick={handleConfirm} disabled={isConfirming}>
            {isConfirming ? <CircularProgress size={22} color="inherit" /> : 'Так'}
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(alreadyDoc)} onClose={() => setAlreadyDoc(null)}>
        <DialogTitle>Документ вже підтверджено!</DialogTitle>
        <DialogContent>
          <DocInfoBlock doc={alreadyDoc} />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <TerminalOrangeButton
            onClick={() => setAlreadyDoc(null)}
            sx={{ bgcolor: 'transparent', color: 'text.primary', boxShadow: 'none' }}
          >
            Закрити
          </TerminalOrangeButton>
          <TerminalOrangeButton
            onClick={() => {
              const id = alreadyDoc?.id;
              setAlreadyDoc(null);
              if (id) navigate(`${routes.docConfirmation}/${id}`);
            }}
          >
            Переглянути
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(scanMessage)} onClose={() => setScanMessage('')}>
        <DialogTitle>Повідомлення</DialogTitle>
        <DialogContent>
          <Typography>{scanMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <TerminalOrangeButton onClick={() => setScanMessage('')}>OK</TerminalOrangeButton>
        </DialogActions>
      </Dialog>
    </TerminalLayout>
  );
};

export default DocConfirmationListPage;
